import React, { useState, useEffect } from 'react';
import { Table, Button, Badge } from 'react-bootstrap';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEye } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { getScheduledNotifications, cancelScheduledNotification } from '../../Api/ApiCalls';
import PaginationControls from '../PaginationControls';
import loader from '../../style/images/loader.gif';
import { toastOptions } from '../../config/Constants';
import NotificationDetailModal from './NotificationDetailModal';
import {Link} from "react-router-dom";

const ScheduledNotificationsList = ({ onNotificationCancelled }) => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [notificationSegmentation, setNotificationSegmentation] = useState(undefined);

    useEffect(() => {
        fetchScheduledNotifications();
    }, [currentPage]);

    const fetchScheduledNotifications = async () => {
        setLoading(true);
        try {
            const response = await getScheduledNotifications(currentPage, 10);
            setNotifications(response.items || []);
            setTotalPages(response.totalPages || 1);
        } catch (error) {
            toast.error('Failed to load scheduled notifications', toastOptions);
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (notification) => {
        setSelectedNotification(notification);
        setNotificationSegmentation(notification?.segmentationCriteria);
        setShowDetailModal(true);
    };

    const formatDateTime = (dateString) => {
        return moment(dateString).format('MMMM D, YYYY [at] h:mm A');
    };

    const getTimeUntil = (dateString) => {
        return moment(dateString).fromNow();
    };

    const handleCancel = async (id) => {
        const result = await Swal.fire({
            title: 'Cancel Scheduled Notification?',
            text: 'This will cancel the scheduled notification. It will be saved as a draft.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel it',
            cancelButtonText: 'No, keep it',
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
        });

        if (result.isConfirmed) {
            try {
                await cancelScheduledNotification(id);
                toast.success('Notification cancelled successfully', toastOptions);
                await fetchScheduledNotifications();

                if (onNotificationCancelled) {
                    onNotificationCancelled();
                }
            } catch (error) {
                toast.error('Failed to cancel notification', toastOptions);
                console.error(error);
            }
        }
    };

    return (
        <div className="card">
            <div className="card-header">
                <h4 className="card-title">Scheduled Notifications</h4>
                <div className="d-flex gap-2">
                    <Link to={"/new-notification"} className="btn btn-secondary">+ New Notification</Link>
                </div>
            </div>
            {loading ? (
                <div className="text-center py-5">
                    <img src={loader} style={{ width: '50px' }} alt="loading" />
                </div>
            ) : notifications.length === 0 ? (
                <div className="card-body text-center py-5">
                    <p className="text-muted mb-0">No scheduled notifications found</p>
                </div>
            ) : (
                <>
                    <div className="card-body">
                        <div className="table-responsive">
                            <Table className="mb-0">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Title (EN)</th>
                                        <th>Type</th>
                                        <th>Scheduled For</th>
                                        <th>Time Until</th>
                                        <th>Recipients</th>
                                        <th className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {notifications.map((notification) => (
                                        <tr key={notification.id}>
                                            <td>{notification.id}</td>
                                            <td>{notification.notificationData?.titleEn || 'N/A'}</td>
                                            <td>
                                                <Badge bg="info" className="text-uppercase">
                                                    {notification.type}
                                                </Badge>
                                            </td>
                                            <td>{formatDateTime(notification.scheduledAt)}</td>
                                            <td>
                                                <Badge bg="warning" text="dark">
                                                    {getTimeUntil(notification.scheduledAt)}
                                                </Badge>
                                            </td>
                                            <td>{notification.totalReceivers || 0}</td>
                                            <td>
                                                <div className="d-flex justify-content-center gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        onClick={() => handleViewDetails(notification)}
                                                        title="View details"
                                                    >
                                                        <FontAwesomeIcon icon={faEye} />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline-danger"
                                                        onClick={() => handleCancel(notification.id)}
                                                        title="Cancel notification"
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>

                    {totalPages > 1 && (
                        <div className="mt-3">
                            <PaginationControls
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    )}
                </>
            )}


            {/* Detail Modal */}
            <NotificationDetailModal
                show={showDetailModal}
                onHide={() => setShowDetailModal(false)}
                notification={selectedNotification}
                segmentation={notificationSegmentation}
                showActionButtons={false}
            />
        </div>
    );
};

export default ScheduledNotificationsList;
